import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMemo, useState } from "react";
import {
  ExpenseCategoryGlPairing,
  useAdminExpenseCategoriesQuery,
} from "@common/utils/graphql";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { DataTable } from "@common/components/Table/DataTable";
import { categoriesTableColumns } from "./columns";
import { CreateCategorySheet } from "./CreateCategorySheet";
import { ViewCategorySheet } from "./ViewCategorySheet";
import { Body } from "@hypercard/web-components";
import { Card } from "@/components/ui/card";

export const CategoriesTab = () => {
  const [searchText, setSearchText] = useState("");
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);

  const openCategoryDrawer = () => setIsCreateCategoryOpen(true);
  const closeCreateCategoryDrawer = () => setIsCreateCategoryOpen(false);

  const [viewCategory, setViewCategory] = useState<
    ExpenseCategoryGlPairing | undefined
  >(undefined);
  const resetViewCategory = () => {
    setViewCategory(undefined);
    refetchCategories();
  };

  const {
    loading: loadingCategories,
    data: categoryDataRaw,
    refetch: refetchCategories,
  } = useAdminExpenseCategoriesQuery({
    fetchPolicy: "cache-and-network",
  });

  const categories = useMemo(() => {
    const doesCategoryMatchSearch = (c: Partial<ExpenseCategoryGlPairing>) => {
      const text = searchText.toLowerCase();
      const matchQuery = [c?.categoryName, c?.glAccount];
      return !!matchQuery.find((q) => q?.toLowerCase().includes(text));
    };
    if (
      categoryDataRaw?.adminExpenseCategories?.__typename !==
      "QueryAdminExpenseCategoriesSuccess"
    ) {
      return [];
    }
    return categoryDataRaw.adminExpenseCategories.data.categories.filter(
      doesCategoryMatchSearch,
    );
  }, [categoryDataRaw, searchText]);

  const header = (
    <div className="flex gap-5 pt-5">
      <Input
        className="w-full"
        type="search"
        placeholder="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
      <Button onClick={openCategoryDrawer}>Create Category</Button>
    </div>
  );

  const table = (
    <div>
      {categories.length > 0 ? (
        <Card className="mt-5">
          <DataTable
            columns={categoriesTableColumns}
            data={categories as ExpenseCategoryGlPairing[]}
            onClick={(row) => setViewCategory(row.original)}
          />
        </Card>
      ) : (
        <Body size="md" weight="regular" className="mt-10 text-center">
          No categories found
        </Body>
      )}
    </div>
  );

  const sheets = (
    <>
      <CreateCategorySheet
        isOpen={isCreateCategoryOpen}
        onClose={closeCreateCategoryDrawer}
        onBack={() => {
          closeCreateCategoryDrawer();
          refetchCategories();
        }}
      />
      <ViewCategorySheet
        isOpen={!!viewCategory}
        category={viewCategory!}
        onClose={resetViewCategory}
      />
    </>
  );

  return (
    <>
      {header}
      {loadingCategories && categories.length === 0 ? (
        <FullPageLoader />
      ) : (
        table
      )}
      {sheets}
    </>
  );
};
